import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';
import DifficultyIcons from '../components/DifficultyIcons';
import BookingModal from '../components/BookingModal';
import QuestionModal from '../components/QuestionModal';
import { formatRu } from '../components/WeekdayCalendar';
import PricePopup from '../components/PricePopup';
import { useSeo } from '../hooks/useSeo';
import '../styles/schedule_page.css';

const MONTHS_RU = ['Января','Февраля','Марта','Апреля','Мая','Июня',
  'Июля','Августа','Сентября','Октября','Ноября','Декабря'];

const pluralMesta = (n) => {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return `${n} мест`;
  if (last === 1) return `${n} место`;
  if (last >= 2 && last <= 4) return `${n} места`;
  return `${n} мест`;
};

function formatDateRange(startDate, endDate) {
  if (!startDate) return '—';
  const s = new Date(startDate);
  const e = endDate ? new Date(endDate) : null;
  const sd = s.getDate();
  const sm = MONTHS_RU[s.getMonth()];
  const sy = s.getFullYear();
  if (!e) return `${sd} ${sm} ${sy}`;
  const ed = e.getDate();
  const em = MONTHS_RU[e.getMonth()];
  const ey = e.getFullYear();
  if (sy === ey && s.getMonth() === e.getMonth()) {
    return `${sd} – ${ed} ${sm}\n${sy}`;
  }
  if (sy === ey) {
    return `${sd} ${sm} –\n${ed} ${em} ${sy}`;
  }
  return `${sd} ${sm} ${sy} –\n${ed} ${em} ${ey}`;
}

function pluralDays(n) {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return `${n} дней`;
  if (last === 1) return `${n} день`;
  if (last >= 2 && last <= 4) return `${n} дня`;
  return `${n} дней`;
}

function calcDuration(startDate, endDate, durationDays) {
  if (durationDays) return pluralDays(Number(durationDays));
  if (startDate && endDate) {
    const diff = Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1;
    return pluralDays(diff);
  }
  return '';
}

function calcEndDate(startDate, endDate, durationDays) {
  if (endDate) return endDate;
  if (startDate && durationDays) {
    const end = new Date(startDate);
    end.setDate(end.getDate() + Number(durationDays) - 1);
    return end.toISOString().slice(0, 10);
  }
  return null;
}

const IconStudy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 3L1 9l11 6 11-6-11-6z"/>
    <path d="M1 9v6"/>
    <path d="M7 11.5V17c1.38.9 3.14 1.5 5 1.5s3.62-.6 5-1.5v-5.5"/>
  </svg>
);

const IconTravel = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 17l4-8 4 4 4-6 4 10"/>
    <path d="M2 20h20"/>
    <path d="M6 20v-3"/>
  </svg>
);

const IconRace = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M8 21h8"/>
    <path d="M12 17v4"/>
    <path d="M7 4h10v7a5 5 0 0 1-10 0V4z"/>
    <path d="M7 7H4a2 2 0 0 0 0 4c.93 0 1.75-.6 2.07-1.46"/>
    <path d="M17 7h3a2 2 0 0 1 0 4c-.93 0-1.75-.6-2.07-1.46"/>
    <path d="M9 17c0-1.1 1.34-2 3-2s3 .9 3 2"/>
  </svg>
);


const Schedule = ({ isPreview = false }) => {
  useSeo({
    title: 'Расписание',
    description: 'Ближайшие курсы обучения яхтингу, парусные путешествия и регаты. Даты, маршруты, инструкторы и свободные места.',
  });
  const { cards, productionCards } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  const yachts = content.yachts || [];
  const instructors = content.instructors || [];
  const [filter, setFilter] = useState('all');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [bookingCard, setBookingCard] = useState(null);
  const [bookingPreselectedDate, setBookingPreselectedDate] = useState(null);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);

  const source = isPreview ? cards : productionCards;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const allEvents = [
    ...(source.adventures || []).filter(c => c.isPublished).map(c => ({ ...c, _type: 'adventure' })),
    ...(source.study || []).filter(c => c.isPublished).map(c => ({ ...c, _type: 'study' })),
    ...(source.races || []).filter(c => c.isPublished).map(c => ({ ...c, _type: 'race' })),
  ].sort((a, b) => {
    if (!a.startDate && !b.startDate) return 0;
    if (!a.startDate) return 1;
    if (!b.startDate) return -1;
    return new Date(a.startDate) - new Date(b.startDate);
  });

  const filtered = filter === 'all' ? allEvents
    : filter === 'study' ? allEvents.filter(e => e._type === 'study')
    : filter === 'adventure' ? allEvents.filter(e => e._type === 'adventure')
    : allEvents.filter(e => e._type === 'race');

  // Expand study cards with multiple date slots into separate rows
  const expandedRows = [];
  for (const event of filtered) {
    if (event.availableDates?.length > 0) {
      for (const slot of event.availableDates) {
        const isoDate     = typeof slot === 'object' ? slot.isoDate : null;
        const spaces      = typeof slot === 'object' ? slot.spaces : null;
        const legacyLabel = typeof slot === 'string' ? slot : null;
        expandedRows.push({ ...event, _slot: { isoDate, spaces, legacyLabel } });
      }
    } else {
      expandedRows.push(event);
    }
  }

  const getRowDate = (row) => {
    if (row._slot?.isoDate) return new Date(row._slot.isoDate + 'T00:00:00');
    if (row.startDate) return new Date(row.startDate);
    return new Date('9999-01-01');
  };

  let displayRows = onlyAvailable
    ? expandedRows.filter(row => {
        if (row._slot) return row._slot.spaces > 0;
        const avail = Number(row.availableSpaces);
        const total = Number(row.totalSpaces);
        return !(total > 0 && avail <= 0);
      })
    : expandedRows;

  displayRows = [...displayRows].sort((a, b) => getRowDate(a) - getRowDate(b));

  const isFull = (row) => {
    if (row._slot) return row._slot.spaces <= 0;
    const avail = Number(row.availableSpaces);
    const total = Number(row.totalSpaces);
    return total > 0 && avail <= 0;
  };

  const openBooking = (row) => {
    if (row._slot) {
      const slotLabel = row._slot.legacyLabel || (row._slot.isoDate ? formatRu(row._slot.isoDate) : null);
      setBookingCard(row);
      setBookingPreselectedDate(slotLabel);
    } else {
      setBookingCard(row);
      setBookingPreselectedDate(null);
    }
  };

  const hasAnyPrice = displayRows.some(r => r.price);

  return (
    <>
      <main className="schedule-page">
      {hasAnyPrice && <PricePopup isPreview={isPreview} />}
      <h1 className="schedule-title">Яхтенный календарь</h1>

      <div className="schedule-filters">
        <div className="schedule-segment">
          {[
            { key: 'all', label: 'Все' },
            { key: 'study', label: 'Обучение' },
            { key: 'adventure', label: 'Путешествие' },
            { key: 'race', label: 'Регата' },
          ].map(f => (
            <button
              key={f.key}
              className={`schedule-segment-btn ${filter === f.key ? 'active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          className={`schedule-available-toggle ${onlyAvailable ? 'active' : ''}`}
          onClick={() => setOnlyAvailable(v => !v)}
        >
          <span className="schedule-available-check">{onlyAvailable ? '✓' : ''}</span>
          Только свободные
        </button>
      </div>

      {displayRows.length === 0 ? (
        <div className="schedule-empty">
          {onlyAvailable ? 'Нет мероприятий со свободными местами' : 'Нет запланированных мероприятий'}
        </div>
      ) : (
        <div className="schedule-table-wrap">
          <table className="schedule-table">
            <thead>
              <tr>
                <th></th>
                <th>Даты</th>
                <th>Мероприятие</th>
                <th>Маршрут</th>
                <th>Яхта</th>
                <th>Инструктор</th>
                <th>Стоимость</th>
                <th>Участники</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, idx) => {
                const full = isFull(row);
                const participants = row.participants || [];
                const _typePath = { adventures: 'adventures', study: 'study', races: 'races', main: 'news' };
                const cardLink = (() => {
                  const path = _typePath[row.type] || 'card';
                  return isPreview ? `/preview/${path}/${row.slug || row.id}` : `/${path}/${row.slug || row.id}`;
                })();

                let dateStr;
                let durStr;
                if (row._slot) {
                  const slotLabel = row._slot.legacyLabel || (row._slot.isoDate ? formatRu(row._slot.isoDate) : '');
                  const computedEnd = calcEndDate(row._slot.isoDate, null, row.durationDays);
                  const endLabel = computedEnd ? ` — ${formatRu(computedEnd)}` : '';
                  dateStr = slotLabel ? `с ${slotLabel}${endLabel}` : '—';
                  durStr = calcDuration(row._slot.isoDate, computedEnd, row.durationDays);
                } else {
                  const isCourse = row._type === 'study';
                  if (isCourse) {
                    dateStr = row.availableDates?.length > 0 ? `${row.availableDates.length} дат` : 'Даты уточняются';
                    durStr = calcDuration(null, null, row.durationDays);
                  } else {
                    const effectiveEnd = calcEndDate(row.startDate, row.endDate, row.durationDays);
                    dateStr = formatDateRange(row.startDate, effectiveEnd);
                    durStr = calcDuration(row.startDate, effectiveEnd, row.durationDays);
                  }
                }

                const spacesDisplay = row._slot
                  ? (row._slot.spaces > 0 ? pluralMesta(row._slot.spaces) : 'Мест нет')
                  : (row.availableSpaces ? `${row.availableSpaces} / ${row.totalSpaces || '?'}` : null);

                return (
                  <tr key={`${row.id}-${idx}`} className={full ? 'sch-row-full' : ''}>
                    <td className="sch-icon">
                      {row._type === 'study' && <IconStudy />}
                      {row._type === 'adventure' && <IconTravel />}
                      {row._type === 'race' && <IconRace />}
                      {row.difficulty > 0 && (
                        <div className="sch-difficulty">
                          <DifficultyIcons
                            difficulty={row.difficulty}
                            sectionKey={row._type === 'adventure' ? 'adventures' : row._type === 'study' ? 'study' : 'races'}
                            size={11}
                          />
                        </div>
                      )}
                    </td>
                    <td className="sch-dates" data-label="Даты">
                      <div className="sch-date-range" style={{ whiteSpace: 'pre-line' }}>
                        {dateStr}
                      </div>
                      {durStr && <div className="sch-duration">{durStr}</div>}
                    </td>
                    <td className="sch-name" data-label="Мероприятие">
                      <Link to={cardLink}>{row.title}</Link>
                    </td>
                    <td className="sch-route" data-label="Маршрут">{row.location || '—'}</td>
                    <td className="sch-yacht" data-label="Яхта">
                      {(() => {
                        const yacht = row.yachtId ? yachts.find(y => String(y.id) === String(row.yachtId)) : null;
                        const name = yacht ? yacht.name : (row.yachtName || null);
                        if (!name) return '—';
                        return yacht
                          ? <Link to={`/yacht/${yacht.name.toLowerCase().replace(/\s+/g, '-')}`} className="sch-link">{name}</Link>
                          : <span>{name}</span>;
                      })()}
                    </td>
                    <td className="sch-instructor" data-label="Инструктор">
                      {(() => {
                        const renderInst = (id, fallbackName) => {
                          const inst = id ? instructors.find(i => String(i.id) === String(id)) : null;
                          const name = inst ? inst.name : (fallbackName || null);
                          if (!name) return null;
                          return inst
                            ? <Link key={id} to={`/instructor/${inst.slug || inst.id}`} className="sch-link">{name}</Link>
                            : <span key={name}>{name}</span>;
                        };
                        const first = renderInst(row.instructorId, row.captain);
                        const second = renderInst(row.instructorId2, row.captain2);
                        if (!first && !second) return '—';
                        return <>{first}{first && second ? ' · ' : ''}{second}</>;
                      })()}
                    </td>
                    <td className="sch-price" data-label="Стоимость">
                      {row.price
                        ? <Link to={isPreview ? '/preview/pricing' : '/pricing'} className="card-price-link">{row.price}</Link>
                        : '—'}
                    </td>
                    <td className="sch-participants" data-label="Участники">
                      {row._slot ? (
                        spacesDisplay && (
                          <span className={`sch-spaces ${full ? 'sch-spaces-full' : ''}`}>
                            {spacesDisplay}
                          </span>
                        )
                      ) : (
                        participants.length > 0
                          ? participants.map((p, i) => (
                              <span key={i} className="sch-participant-name">{p}</span>
                            ))
                          : <span className="sch-no-participants">—</span>
                      )}
                    </td>
                    <td className="sch-action">
                      {full ? (
                        <button className="btn-full">Свободных<br/>мест нет</button>
                      ) : (
                        <button
                          className="btn-signup-table"
                          onClick={() => openBooking(row)}
                        >
                          {row._type === 'adventure' ? 'Записаться в путешествие'
                            : row._type === 'race' ? 'Записаться на регату'
                            : 'Записаться на курс'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BookingModal
        isOpen={!!bookingCard}
        onClose={() => { setBookingCard(null); setBookingPreselectedDate(null); }}
        card={bookingCard}
        preselectedDate={bookingPreselectedDate}
      />
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Расписание" />
      </main>
      <section className="cta-section">
        <h2>Остались вопросы?</h2>
        <p>Напишите нам — поможем с выбором даты и программы. Ответим в течение дня.</p>
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
      </section>
    </>
  );
};

export default Schedule;
